/**
 * Shared blog-content normalization helpers.
 *
 * The agent's `output_blog` tool emits the blog title BOTH as its own `title`
 * field AND as a `# Title` heading at the top of `content`. Every surface that
 * renders the title separately from the body (the right-panel editor, the chat
 * thread preview, the published WordPress post) must strip that leading heading
 * or the title renders twice. Keeping this logic in one module ensures all those
 * surfaces stay in sync.
 */

/** Normalize a heading/title for comparison: drop the leading #'s and emphasis. */
export const normalizeHeading = (s: string): string =>
  (s || "")
    .replace(/^#{1,6}\s+/, "")
    .replace(/[*_`]/g, "")
    .trim()
    .toLowerCase();

/**
 * Remove a leading Markdown heading from the body when it just repeats the title.
 * Loops so a title that is accidentally repeated more than once at the top is
 * fully removed. Returns the content unchanged when there is no duplicate.
 */
export const stripDuplicateTitleHeading = (
  content: string,
  title: string
): string => {
  const target = normalizeHeading(title);
  if (!target || !content) return content;

  const lines = content.split("\n");
  let i = 0;
  let removed = false;
  while (i < lines.length) {
    const line = lines[i].trim();
    if (line === "") {
      i++;
      continue;
    }
    if (/^#{1,6}\s+/.test(line) && normalizeHeading(line) === target) {
      i++;
      removed = true;
      while (i < lines.length && lines[i].trim() === "") i++;
      continue;
    }
    break;
  }
  return removed ? lines.slice(i).join("\n").trimStart() : content;
};

/**
 * Render a structured blog payload as the Markdown shown inline in the chat thread:
 * the title as a single `# Title` heading followed by the body (with any duplicate
 * title heading stripped from the body). Shared by the live stream and the reopened
 * conversation-history path so both render the blog identically.
 */
export const blogToChatMarkdown = (
  blog: { title?: string; content?: string }
): string => {
  const title = (blog.title || "").trim();
  const body = stripDuplicateTitleHeading((blog.content || "").trim(), title).trim();
  const heading = title ? `# ${title}\n\n` : "";
  return `${heading}${body}`.trim();
};
